import { trimTrailingZeros } from '@utils/formatting-utils';

export default class FormattingService {
  public static formatEnumToBackend(value: string): string {
    return value
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  /**
   *
   * @param {string} value - The value to be formatted
   * @returns - The formatted value as a string with the first letter of each word capitalized and joined by spaces.
   *
   * @example
   * // Returns 'Non Proportional'
   * formatEnum('NON_PROPORTIONAL');
   *
   * @example
   * // Returns 'Property'
   * formatEnum('PROPERTY');
   */
  public static formatEnumToDisplay(value: string, useSlash = false): string {
    if (!value) {
      return '';
    }

    const delimiter = useSlash ? '/' : ' ';

    return value
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(delimiter);
  }

  public static formatCustomCurrency(
    numberValue: number,
    customCurrencySymbol?: string,
    decimals = 2,
    shouldTrimTrailingZeros = false
  ): string {
    const stringValue = numberValue.toString().replace(/,/g, '');
    const [integerPart, decimalPart = ''] = stringValue.split('.');
    const formattedIntegerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ','
    );
    let formattedNumber = `${formattedIntegerPart}${
      decimalPart ? `.${decimalPart}` : ''
    }`;

    if (shouldTrimTrailingZeros) {
      formattedNumber =
        trimTrailingZeros(formattedNumber, decimals, true).toString() || '0';
    }

    // Manually append the custom currency symbol
    return `${customCurrencySymbol} ${formattedNumber}`.trim();
  }

  /**
   * Converts a camel- or Pascal-cased string (e.g. "RequestedByInsured")
   * into an UPPER_SNAKE_CASE string (e.g. "REQUESTED_BY_INSURED").
   *
   * @param {string} value - The string to convert.
   * @returns {string} The resulting UPPER_SNAKE_CASE string.
   */
  public static toUpperSnakeCase(value: string): string {
    if (!value) return '';

    // Insert underscores before uppercase letters, then remove any leading underscore,
    // and finally convert to uppercase.
    const snakeCased = value.replace(/([A-Z])/g, '_$1').replace(/^_/, '');

    return snakeCased.toUpperCase();
  }

  /**
   * Formats a number or numeric string with commas as thousand separators.
   *
   * @param {number | string} value - The number (or string containing a number) to format.
   * @returns {string} The formatted number with commas, or the original string if invalid.
   *
   * @example
   * // Returns "1,234"
   * formatNumberWithCommas(1234);
   *
   * @example
   * // Returns "12,345.678"
   * formatNumberWithCommas("12345.678");
   */

  public static formatNumberWithCommas(value: number | string): string {
    if (value === null || value === undefined || value === '') return '';

    let num = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(num)) num = Number(num);

    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  }

  /**
   * Converts a camelCase or PascalCase string into a space-separated string
   * with each word capitalized.
   *
   * @param {string} value - The camelCase or PascalCase string to convert.
   * @returns {string} The resulting string with spaces.
   *
   * @example
   * // Returns 'Requested By Insured'
   * formatCamelCaseToDisplay('RequestedByInsured');
   */
  public static formatCamelCaseToDisplay(value: string): string {
    if (!value) return '';

    // This regex finds uppercase letters that are not at the beginning and adds a space before them.
    return value.replace(/(?!^)([A-Z])/g, ' $1');
  }

  /**
   * Parses a currency string and returns its numeric value.
   * Removes any non-numeric characters except "-" and ".".
   *
   * @param {string | null | undefined} value - The string to parse as currency.
   * @returns {number | undefined} - The numeric value or undefined if parsing fails.
   *
   * @example
   * // Returns 1234.56
   * parseCurrency('$1,234.56');
   *
   * @example
   * // Returns -987
   * parseCurrency('-987 USD');
   *
   * @example
   * // Returns undefined
   * parseCurrency('abc');
   */
  public static parseCurrency(value?: string | null): number | undefined {
    if (!value) return undefined;

    const numericValue = Number(String(value).replace(/[^\d.-]+/g, ''));

    return Number.isFinite(numericValue) ? numericValue : undefined;
  }
}
